
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Card } from "react-bootstrap";

import { GanttColumn } from "../../../Components/GantV2/types";

import { useAuth } from "../../../Hooks/UseAuth";
import useTranslation from "../../../Hooks/UseTranslation";
import { useProject } from "../../../Hooks/UseProject";
import { useProgrammation } from "../../../Hooks/UseProgrammation";
import { useProjection } from "@/Hooks/ProjectionProvider";
import { useTeams } from "@/Hooks/UseTeam";

import { getWeekNavigator } from "../../../Helpers/LookaheadHelper";
import TeamsHelper from "../../../Helpers/TeamsHelper";
import { maxDate } from "@/Helpers/DateFunctions";
import {
  IGetItemsListOptions,
  filterByColorCodes,
  filterByGroups,
  filterByStatus,
  filterByTypes,
  filterByUsers,
  getCollapsedGroups,
  getCollapsedTasks,
  getGanttItems,
  getNonWorkingDays,
  getWeeklyResult,
  saveCollapsedGroups,
  saveCollapsedTasks
} from "../../../Helpers/ProgrammationHelper";
import History from "../../../Helpers/History";

import ProjectsActions from "../../../Redux/Actions/ProjectsActions";
import InterfaceActions from "../../../Redux/Actions/InterfaceActions";
import { regenerateWeekly, setWeeklyAction, setWeeklyCollapsedGroupsAction, setWeeklyCollapsedTasksAction, setWeeklyGanttItemsAction } from "../../../Redux/Actions/WeeklyActions";

import { getStatusName } from "../../../Components/TaskManager/TaskEditorHelper";
import Gantt from "../../../Components/GantV2/Gantt";
import TaskFilters from "../../../Components/TaskFilters/TaskFilters";
import UserAvatar from "../../../Components/UserAvatar/UserAvatar";
import PdfExportModal from "../../../Components/PdfExportModal/PdfExportModal";
import ProgrammationManager from "../../../Components/ProgrammationManager/ProgrammationManager";
import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";
import ProgrammationHeader from "../ProgrammationHeader";
import ProgrammationCreateMenu from "../ProgrammationCreateMenu";
import WeekNavigator from "@/Components/WeekNavigator/WeekNavigator";
import TasksImporterUnified from "@/Components/TasksImporterUnified/TasksImporterUnified";
import ResponsivePageToolbar from "@/Layouts/MainLayout/components/ResponsivePageToolbar";
import WeeklyPlanStatusBar, { WeeklyPlanLoadStatus } from "@/Components/WeeklyPlanStatusBar/WeeklyPlanStatusBar";
import { getLeanPeriodKey } from "@/Helpers/LeanWeekHelper";
import { getWeeklyCommitmentCandidates } from "@/Helpers/WeeklyCommitmentHelper";
import WeeklyCommitmentsModal from "@/Components/WeeklyCommitmentsModal/WeeklyCommitmentsModal";
import WeeklyFailureAnalysisModal from "@/Components/WeeklyFailureAnalysisModal/WeeklyFailureAnalysisModal";
import {
  loadWeeklyWorkPlanPeriod,
  closeWeeklyWorkPlan,
  declareWeeklyCommitmentComplete,
  publishWeeklyWorkPlan,
  resolveWeeklyCommitment,
  saveWeeklyWorkPlanDraft,
  WeeklyCommitment,
  WeeklyWorkPlan,
  WeeklyWorkPlanDraftCommitment,
} from "@/Services/WeeklyWorkPlanService";
import {
  analyzeCommitmentFailure,
  CommitmentFailure,
  createCorrectiveAction,
  loadCommitmentFailures,
  loadProjectCncCatalog,
  ProjectCncCatalogRow,
} from "@/Services/CncLearningService";

const WeeklyPage = (props: any) => {

  const { t } = useTranslation()

  const sidebarRefBtn = useRef<HTMLButtonElement>(null)

  const dispatch = useDispatch()

  const { userData, ability } = useAuth()

  const { project_id, project, projectLoaded, loadProject } = useProject()
  const { programmationLoaded, loadProgrammation, openItem, createNewTask, createNewGroup } = useProgrammation()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()

  const { teams, allInternalProfiles } = useTeams()

  const week = props.match.params.week

  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const weekly = useSelector((state: any) => state.WeeklyReducer)

  const teamsMembersIds = useMemo(() => TeamsHelper.getAllMembersFromProject(teams).map((user: any) => user._id), [ teams ])

  const [ showTaskFilters, setShowTaskFilters ] = useState(false)
  const [ showTasksImporter, setShowTasksImporter ] = useState(false)

  const savedFilters = localStorage.getItem(`${project_id}-weekly-filters`)

  const [ filters, setFilters ] = useState(savedFilters ? JSON.parse(savedFilters) : {
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const [ mySettings, setMySettings ] = useState(project?.mySettings)
  const [ showSidebar, setShowSidebar ] = useState(false)
  const [ weeklyPlanLoadStatus, setWeeklyPlanLoadStatus ] = useState<WeeklyPlanLoadStatus>('idle')
  const [ weeklyPlan, setWeeklyPlan ] = useState<WeeklyWorkPlan | null>(null)
  const [ weeklyCommitments, setWeeklyCommitments ] = useState<WeeklyCommitment[]>([])
  const [ weeklyPlanReload, setWeeklyPlanReload ] = useState(0)
  const [ weeklyPlanSaving, setWeeklyPlanSaving ] = useState(false)
  const [ weeklyPlanPublishing, setWeeklyPlanPublishing ] = useState(false)
  const [ weeklyPlanClosing, setWeeklyPlanClosing ] = useState(false)
  const [ weeklyCommitmentProcessing, setWeeklyCommitmentProcessing ] = useState('')
  const [ weeklyPlanActionError, setWeeklyPlanActionError ] = useState('')
  const [ showWeeklyCommitments, setShowWeeklyCommitments ] = useState(false)
  const [ commitmentFailures, setCommitmentFailures ] = useState<CommitmentFailure[]>([])
  const [ cncCatalog, setCncCatalog ] = useState<ProjectCncCatalogRow[]>([])
  const [ selectedFailure, setSelectedFailure ] = useState<{ failure: CommitmentFailure, taskName: string } | null>(null)
  const [ failureSaving, setFailureSaving ] = useState(false)
  const [ failureError, setFailureError ] = useState('')

  const navigator = useMemo(() => {

    if (!project._id) return {
      prev: '',
      next: '',
      startDay: '',
      endDay: '',
      week: 0,
      range: []
    }

    return getWeekNavigator({
      projectStart: project.startsAt,
      projectEnd: maxDate(project.realEnd, projectedEndDate) || project.realEnd,
      weeksSize: 1,
      week
    })
  }, [ project, week, projectedEndDate ])

  const leanPeriodKey = useMemo(
    () => getLeanPeriodKey(navigator.week, navigator.startDay),
    [ navigator.week, navigator.startDay ],
  )

  const isPastLeanPeriod = useMemo(() => Boolean(
    navigator.endDay && moment(navigator.endDay).endOf('day').isBefore(moment()),
  ), [ navigator.endDay ])

  const canManageWeeklyPlan = `${project.user?._id || project.user}` === userData?.id
    || project.assistants?.some((assistant: any) => `${assistant._id || assistant}` === userData?.id)

  const weeklyCommitmentCandidates = useMemo(
    () => getWeeklyCommitmentCandidates(weekly.result, navigator.startDay, navigator.endDay),
    [ weekly.result, navigator.startDay, navigator.endDay ],
  )

  const failedCommitmentKey = useMemo(() => weeklyCommitments
    .filter((commitment) => commitment.result === 'not_kept' || commitment.result === 'withdrawn')
    .map((commitment) => commitment._id)
    .sort()
    .join(','), [ weeklyCommitments ])

  const refreshCommitmentFailures = async () => {
    if (!project_id) return
    const failures = await loadCommitmentFailures(project_id)
    setCommitmentFailures(failures)
    setSelectedFailure((current) => {
      if (!current) return null
      const updated = failures.find((failure) => failure._id === current.failure._id)
      return updated ? { ...current, failure: updated } : current
    })
  }

  useEffect(() => {
    if (!project_id || !failedCommitmentKey) {
      setCommitmentFailures([])
      return
    }

    Promise.all([
      loadCommitmentFailures(project_id),
      loadProjectCncCatalog(project_id),
    ]).then(([ failures, catalog ]) => {
      setCommitmentFailures(failures)
      setCncCatalog(catalog)
    }).catch(() => setWeeklyPlanActionError(t('WEEKLY_PLAN:LoadCncError')))
  }, [ project_id, failedCommitmentKey ])

  useEffect(() => {
    let active = true

    if (!project_id || !leanPeriodKey) {
      setWeeklyPlanLoadStatus('idle')
      setWeeklyPlan(null)
      setWeeklyCommitments([])
      return () => { active = false }
    }

    setWeeklyPlanLoadStatus('loading')

    loadWeeklyWorkPlanPeriod(project_id, leanPeriodKey)
      .then(({ plan, commitments }) => {
        if (!active) return
        setWeeklyPlan(plan)
        setWeeklyCommitments(commitments)
        setWeeklyPlanLoadStatus('loaded')
      })
      .catch(() => {
        if (!active) return
        setWeeklyPlan(null)
        setWeeklyCommitments([])
        setWeeklyPlanLoadStatus('error')
      })

    return () => { active = false }
  }, [ project_id, leanPeriodKey, weeklyPlanReload ])

  useEffect(() => {
    setShowWeeklyCommitments(false)
    setWeeklyPlanActionError('')
  }, [ leanPeriodKey ])

  const saveWeeklyDraft = async (commitments: WeeklyWorkPlanDraftCommitment[], retrospectiveReason?: string) => {
    if (!project_id || !leanPeriodKey) return

    setWeeklyPlanSaving(true)
    setWeeklyPlanActionError('')

    try {
      const result = await saveWeeklyWorkPlanDraft({
        projectId: project_id,
        periodKey: leanPeriodKey,
        expectedVersion: weeklyPlan?.version,
        retrospectiveReason,
        commitments,
      })
      setWeeklyPlan(result.plan)
      setWeeklyCommitments(result.commitments)
      setWeeklyPlanLoadStatus('loaded')
    } catch (error) {
      setWeeklyPlanActionError(t('WEEKLY_PLAN:SaveDraftError'))
      throw error
    } finally {
      setWeeklyPlanSaving(false)
    }
  }

  const publishCurrentWeeklyPlan = async (retrospectiveReason?: string) => {
    if (!weeklyPlan || weeklyPlan.status !== 'draft') return

    setWeeklyPlanPublishing(true)
    setWeeklyPlanActionError('')

    try {
      const result = await publishWeeklyWorkPlan(weeklyPlan._id, weeklyPlan.version, retrospectiveReason)
      setWeeklyPlan(result.plan)
      setWeeklyCommitments(result.commitments)
      setWeeklyPlanLoadStatus('loaded')
    } catch (error) {
      setWeeklyPlanActionError(t('WEEKLY_PLAN:PublishPlanError'))
      throw error
    } finally {
      setWeeklyPlanPublishing(false)
    }
  }

  const replaceWeeklyCommitment = (updated: WeeklyCommitment) => {
    setWeeklyCommitments((current) => current.map((commitment) => (
      commitment._id === updated._id ? updated : commitment
    )))
  }

  const declareCommitmentComplete = async (commitmentId: string, completedAt?: string) => {
    setWeeklyCommitmentProcessing(commitmentId)
    setWeeklyPlanActionError('')
    try {
      replaceWeeklyCommitment(await declareWeeklyCommitmentComplete(commitmentId, { completedAt }))
    } catch (error) {
      setWeeklyPlanActionError(t('WEEKLY_PLAN:CommitmentActionError'))
      throw error
    } finally {
      setWeeklyCommitmentProcessing('')
    }
  }

  const resolveCommitment = async (commitmentId: string, accepted: boolean, comment?: string) => {
    setWeeklyCommitmentProcessing(commitmentId)
    setWeeklyPlanActionError('')
    try {
      const updated = await resolveWeeklyCommitment(commitmentId, { accepted, comment })
      replaceWeeklyCommitment(updated)
      if (updated.result === 'not_kept' || updated.result === 'withdrawn') {
        await refreshCommitmentFailures()
        setCncCatalog(await loadProjectCncCatalog(project_id))
      }
    } catch (error) {
      setWeeklyPlanActionError(t('WEEKLY_PLAN:CommitmentActionError'))
      throw error
    } finally {
      setWeeklyCommitmentProcessing('')
    }
  }

  const closeCurrentWeeklyPlan = async () => {
    if (!weeklyPlan || weeklyPlan.status !== 'published') return

    setWeeklyPlanClosing(true)
    setWeeklyPlanActionError('')
    try {
      const result = await closeWeeklyWorkPlan(weeklyPlan._id)
      setWeeklyPlan(result.plan)
      setWeeklyCommitments(result.commitments)
      setWeeklyPlanLoadStatus('loaded')
    } catch (error) {
      setWeeklyPlanActionError(t('WEEKLY_PLAN:ClosePlanError'))
      throw error
    } finally {
      setWeeklyPlanClosing(false)
    }
  }

  const analyzeSelectedFailure = async (input: { primaryCauseId: string, explanation: string, rootCause: string }) => {
    if (!selectedFailure) return
    setFailureSaving(true)
    setFailureError('')
    try {
      const updated = await analyzeCommitmentFailure(selectedFailure.failure._id, input)
      setCommitmentFailures((current) => current.map((failure) => failure._id === updated._id ? updated : failure))
      setSelectedFailure({ ...selectedFailure, failure: updated })
    } catch (error) {
      setFailureError(t('WEEKLY_PLAN:SaveCncError'))
      throw error
    } finally {
      setFailureSaving(false)
    }
  }

  const createSelectedFailureAction = async (input: { title: string, description: string, owner: string, dueDate: string }) => {
    if (!selectedFailure) return
    setFailureSaving(true)
    setFailureError('')
    try {
      await createCorrectiveAction({
        projectId: project_id,
        failureIds: [ selectedFailure.failure._id ],
        ...input,
      })
      await refreshCommitmentFailures()
    } catch (error) {
      setFailureError(t('WEEKLY_PLAN:SaveActionError'))
      throw error
    } finally {
      setFailureSaving(false)
    }
  }

  const navigateToWeek = (week?: any) => {

    dispatch(regenerateWeekly())

    if (week) {

      History.push(`/project/${project_id}/programmation-weekly/${week}`)
    } else {

      History.push(`/project/${project_id}/programmation-weekly`)
    }
  }

  const nonWorkingDays = useMemo(() => {

    if (!project._id) return []

    return getNonWorkingDays(weekly.start, weekly.end, project)
  }, [ project._id, weekly.start, weekly.end ])

  const [ showPdfExportModal, setShowPdfExportModal ] = useState(false)

  const handleTaskListImported = () => {
    loadProject()
    loadProgrammation()
    setShowTasksImporter(false)
  }

  const columns: GanttColumn[] = [
    {
      label: t('WEEKLY:TypeColumn'),
      field: 'type',
      width: 50,
      render: (field: any) => !field ? null : field.substring(0, 1).toUpperCase()
    },
    {
      label: t('WEEKLY:ColorCodeColumn'),
      field: 'colorCodeName',
      width: 60,
    },
    {
      label: t('WEEKLY:PositionColumn'),
      field: 'position',
      width: 100,
    },
    {
      label: t('WEEKLY:TitleColumn'),
      field: 'name',
      width: 300,
    },
    {
      label: t('WEEKLY:StatusColumn'),
      field: 'status',
      width: 100,
      render: (field: any) => getStatusName(field)
    },
    {
      label: t('WEEKLY:StartsAtColumn'),
      field: 'startsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:EndsAtColumn'),
      field: 'endsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ResponsiblesColumn'),
      field: 'responsibles',
      width: 80,
      render: (field: any, item: any) => {

        const isField = !!field
        const isFieldArray = isField && Array.isArray(field)

        if (isField && isFieldArray) {

          const responsibles = field.filter((user: any) => teamsMembersIds.includes(user._id))

          return responsibles.map((user: any) => <UserAvatar key={user._id} user={user} size="xs" />)
        }

        return null
      }
    },
    {
      label: t('WEEKLY:ReleasesAtColumn'),
      field: 'releasesAt',
      width: 80,
      render: (field: any) => field ? moment.utc(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ActualStartColumn'),
      field: 'startedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ActualEndColumn'),
      field: 'endedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:CommentsColumn'),
      field: 'comments',
      width: 150,
      render: (field: any) => {

        if (!field) return null

        return `${field.comment}...`
      }
    }
  ]

  const loadWeekly = (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      const responsibles = task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp))

      return {
        ...task,
        responsibles,
        internalProfiles: task.internalProfiles.map((resp: any) => allInternalProfiles.find((profile: any) => profile._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      navigator,
      nonWorkingDays: project.nonWorkingDays,
    }

    let weeklyResult = getWeeklyResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      weeklyResult = filterByUsers(weeklyResult, users_ids)
    } else if (newFilters.user)
      weeklyResult = filterByUsers(weeklyResult, newFilters.user.split(','))

    if (newFilters.group) {

      weeklyResult = filterByGroups(weeklyResult, newFilters.group.split(','))
    } else if (newFilters.code)
      weeklyResult = filterByColorCodes(weeklyResult, newFilters.code.split(','))

    if (newFilters.type)
      weeklyResult = filterByTypes(weeklyResult, newFilters.type.split(','))

    if (newFilters.state)
      weeklyResult = filterByStatus(weeklyResult, newFilters.state.split(','))

    const collapsedGroups = getCollapsedGroups(project_id, 'weekly')
    const collapsedTasks = getCollapsedTasks(project_id, 'weekly')

    dispatch(setWeeklyAction(weeklyResult, navigator.startDay, navigator.endDay, navigator.week, collapsedGroups, collapsedTasks))
  }

  const hasActiveFilters = () => {

    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project_id}-weekly-filters`, JSON.stringify(filters))

    loadWeekly(filters)
    setFilters(filters)
  }

  const handleEditItem = async (item_id: string) => {

    openItem({ item_id, schema: 'weekly' })
  }

  const handleToggleCollapse = async (group_id: string) => {

    const collapsedGroups = getCollapsedGroups(project_id, 'weekly')

    if (collapsedGroups.includes(group_id)) {

      collapsedGroups.splice(collapsedGroups.indexOf(group_id), 1)
    } else {

      collapsedGroups.push(group_id)
    }

    saveCollapsedGroups(project_id, 'weekly', collapsedGroups)

    dispatch(setWeeklyCollapsedGroupsAction(collapsedGroups))
  }

  const handleToggleCollapseTask = async (task_id: string) => {

    const collapsedTasks = getCollapsedTasks(project_id, 'weekly')

    if (collapsedTasks.includes(task_id)) {

      collapsedTasks.splice(collapsedTasks.indexOf(task_id), 1)
    } else {

      collapsedTasks.push(task_id)
    }

    saveCollapsedTasks(project_id, 'weekly', collapsedTasks)

    dispatch(setWeeklyCollapsedTasksAction(collapsedTasks))
  }

  const handleShowWorkflows = (groups: string[], tasks: any[]) => {

    saveCollapsedGroups(project_id, 'weekly', groups)
    saveCollapsedTasks(project_id, 'weekly', tasks)

    dispatch(setWeeklyCollapsedGroupsAction(groups))
    dispatch(setWeeklyCollapsedTasksAction(tasks))
  }

  const toggleSidebar = (target?: any) => {

    if (!target) {

      if (showSidebar) updateSettings()

      setShowSidebar(!showSidebar)
    } else {

      if (sidebarRefBtn.current) {

        const isButtonClicked = sidebarRefBtn.current.contains(target) || target.closest?.('.pl4-settings-trigger')

        if (!isButtonClicked) {

          // trigger click because if not mySettings is not updated
          sidebarRefBtn.current.click()
        }
      }
    }
  }

  const handleUpdateSetting = async (key: string, value: any) => {

    let field = key

    if (field === 'dayWidth') {

      field = 'ganttWeeklyColumnSize'
    } else if (field === 'rowHeight') {

      field = 'ganttWeeklyRowSize'
    } else if (field === 'hideNonWorkingDays') {

      field = 'ganttWeeklyHideNonWorkingDays'
    } else if (field === 'showVerticalBorders') {

      field = 'ganttWeeklyShowVerticalBorders'
    } else if (field === 'columnsHidden') {

      field = 'ganttHideWeeklyFields'
    } else if (field === 'divisorPosition') {

      field = 'ganttWeeklyDivisorPosition'
    }

    await updateSettings({
      ...mySettings,
      [ field ]: value
    })
  }

  const updateSettings = async (settings = mySettings) => {

    let areDifferent = false

    for (const key in settings) {

      if (key !== 'setted' && settings[ key ] !== project.mySettings[ key ]) {

        areDifferent = true
      }
    }

    if (areDifferent) {

      await dispatch(ProjectsActions.updateSettings(project.mySettings._id, settings))
    }

    setMySettings(settings)
  }

  useEffect(() => {

    if (projectLoaded) {


      setMySettings(project.mySettings)
    }
  }, [ projectLoaded ])

  useEffect(() => {

    if (projectLoaded) {

      if (programmation.loadStatus === 'none') {

        loadProgrammation()
      }
    }
  }, [ projectLoaded, programmation.loadStatus ])

  useEffect(() => {

    if (!programmationLoaded) return

    if (weekly.loadStatus === 'none') {

      loadWeekly()
    }
  }, [ programmationLoaded, weekly.loadStatus ])

  // ganttitems
  useEffect(() => {

    if (weekly.loadStatus !== 'loaded') return
    if (weekly.loadGanttItemsStatus !== 'none') return

    const options: IGetItemsListOptions = {
      itemsList: weekly.result,
      collapsedGroups: weekly.collapsedGroups,
      collapsedTasks: weekly.collapsedTasks,
      schema: 'weekly',
      nonWorkingDays: extendedNonWorkingDays,
      stretchUpToday: mySettings?.stretchUpToday,
      moveToScheduledDays: mySettings?.moveToScheduledDays,
      showOnlyThisUserTasks: project.mySettings.showOnlyMyTasks ? userData?.id : undefined,
      week: navigator.week,
      ganttStart: weekly.start,
      ganttEnd: weekly.end,
    }

    const ganttItems = getGanttItems(options);

    // cortamos las barras de los items que se salen del rango de fechas
    ganttItems.forEach((item: any) => {

      item.barStart = moment(item.barStart).isBefore(moment(weekly.start)) ? weekly.start : item.barStart;
      item.barEnd = moment(item.barEnd).isAfter(moment(weekly.end)) ? weekly.end : item.barEnd;

      item.bars?.forEach((bar: any) => {

        bar.barStart = moment(bar.barStart).isBefore(moment(weekly.start)) ? weekly.start : bar.barStart;
        bar.barEnd = moment(bar.barEnd).isAfter(moment(weekly.end)) ? weekly.end : bar.barEnd;
      })
    })

    dispatch(setWeeklyGanttItemsAction(ganttItems))
  }, [ weekly.loadGanttItemsStatus, weekly.result, mySettings ]);

  useEffect(() => {

    const taskListLoaded = weekly.loadStatus === 'loaded'

    if (!projectLoaded || !taskListLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }

  }, [ projectLoaded, weekly.loadStatus ])

  return (
    <ProjectLayout menuName="programmation" subMenuName="weekly" hideSubMenu>
      <ProgrammationHeader
        activeTab="weekly"
        projectId={project_id}
        subtitle={`${t('WEEKLY:Title')} · ${project.name || ''}`}
      >
        {/* <div className='ul-widget__head-label flex-grow-1'>
          <h3 className="ul-widget__head-title">
            <span>
              {t('WEEKLY:Title')}
            </span>
            {(weekly.start !== '' && weekly.end !== '') && (
              <small className="d-block d-sm-inline">
                ({t('WEEKLY:DateFrom')} {' '}
                {moment(weekly.start).format('DD/MM/YYYY')} {' '}
                {t('WEEKLY:DateTo')} {' '}
                {moment(weekly.end).format('DD/MM/YYYY')})
              </small>
            )}
          </h3>
        </div> */}
        <ResponsivePageToolbar
          className="pl4-programmation-toolbar"
          actionsClassName="pl4-programmation-toolbar__actions"
          primaryAction={ability.can('create', 'task') ? (
              <ProgrammationCreateMenu
                disabled={!projectLoaded || !project._id || !programmationLoaded}
                importDisabled={programmation.list.length > 0 || hasActiveFilters()}
                onCreateTask={() => createNewTask()}
                onCreateGroup={() => createNewGroup()}
                onImport={() => setShowTasksImporter(true)}
              />
          ) : undefined}
          temporalControl={(
            <WeekNavigator
              prev={navigator.prev}
              next={navigator.next}
              current={navigator.week}
              label={`${t('RESOURCES_CNC:TitleWeek')} ${navigator.week ? Number(String(navigator.week).slice(4)) : ''}`}
              range={navigator.startDay && navigator.endDay ? `${moment(navigator.startDay).format('DD/MM')} - ${moment(navigator.endDay).format('DD/MM/YYYY')}` : ''}
              startDate={navigator.startDay}
              endDate={navigator.endDay}
              onChangeWeek={navigateToWeek}
            />
          )}
          actions={[
            {
              id: 'filter',
              label: t('WEEKLY:FilterButton'),
              icon: <span className="icon-filtro" aria-hidden="true" />,
              onClick: () => setShowTaskFilters(true),
              active: hasActiveFilters(),
              className: 'pl4-programmation-action',
            },
            {
              id: 'export',
              label: t('WEEKLY:ExportButton'),
              icon: <i className="fas fa-download" aria-hidden="true" />,
              onClick: () => setShowPdfExportModal(true),
              className: 'pl4-programmation-action',
            },
            {
              id: 'settings',
              label: t('WEEKLY:SettingsButton'),
              icon: <span className="icon-configuracion" aria-hidden="true" />,
              onClick: () => toggleSidebar(),
              active: showSidebar,
              buttonRef: sidebarRefBtn,
              className: 'pl4-programmation-action pl4-settings-trigger',
              mobileClassName: 'pl4-settings-trigger',
            },
          ]}
        />
      </ProgrammationHeader>
      <ContentBody noScroll>
        <div className="pl4-weekly-workspace">
          <WeeklyPlanStatusBar
            loadStatus={weeklyPlanLoadStatus}
            plan={weeklyPlan}
            commitments={weeklyCommitments}
            onRetry={() => setWeeklyPlanReload((value) => value + 1)}
            actionLabel={weeklyPlan ? t('WEEKLY_PLAN:ViewCommitments') : t('WEEKLY_PLAN:PreparePlan')}
            onAction={() => setShowWeeklyCommitments(true)}
          />
          <Card className="table-card pl4-weekly-workspace__content">
            {project._id && (
              <Gantt
                schema="weekly"
                start={weekly.start}
                end={weekly.end}
                items={weekly.ganttItems}
                columns={columns}
                nonWorkingDays={extendedNonWorkingDays}
                dayWidth={mySettings?.ganttWeeklyColumnSize || 100}
                maxDayWidth={240}
                rowHeight={mySettings?.ganttWeeklyRowSize || 32}
                maxRowHeight={160}
                hideNonWorkingDays={mySettings?.ganttWeeklyHideNonWorkingDays || false}
                showVerticalBorders={mySettings?.ganttWeeklyShowVerticalBorders || false}
                divisorPosition={mySettings?.ganttWeeklyDivisorPosition}
                useCodeColors={mySettings?.useCodeColors || false}
                stretchUpToday={mySettings?.stretchUpToday || false}
                moveToScheduledDays={mySettings?.moveToScheduledDays || false}
                columnsHidden={mySettings?.ganttHideWeeklyFields || []}
                showSidebar={showSidebar}
                onSelectItem={handleEditItem}
                onToggleCollapse={handleToggleCollapse}
                onToggleCollapseTask={handleToggleCollapseTask}
                onShowWorkflows={handleShowWorkflows}
                onClickOutside={toggleSidebar}
                onUpdateSetting={handleUpdateSetting}
                sortEnabled={false}
                lineBaseTasks={false}
              />
            )}
          </Card>
        </div>
      </ContentBody>

      <TaskFilters
        show={showTaskFilters}
        type="weekly"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />

      <ProgrammationManager taskList={weekly.result} />

      <WeeklyCommitmentsModal
        key={`${leanPeriodKey}-${weeklyPlan?._id || 'new'}-${weeklyPlan?.version || 0}`}
        show={showWeeklyCommitments && !selectedFailure}
        candidates={weeklyCommitmentCandidates}
        commitments={weeklyCommitments}
        plan={weeklyPlan}
        periodStart={navigator.startDay}
        periodEnd={navigator.endDay}
        isPastPeriod={isPastLeanPeriod}
        saving={weeklyPlanSaving}
        publishing={weeklyPlanPublishing}
        closing={weeklyPlanClosing}
        processingCommitmentId={weeklyCommitmentProcessing}
        canManagePlan={canManageWeeklyPlan}
        currentUserId={userData?.id || ''}
        failures={commitmentFailures}
        error={weeklyPlanActionError}
        onHide={() => setShowWeeklyCommitments(false)}
        onSave={saveWeeklyDraft}
        onPublish={publishCurrentWeeklyPlan}
        onDeclareComplete={declareCommitmentComplete}
        onResolve={resolveCommitment}
        onClosePlan={closeCurrentWeeklyPlan}
        onOpenFailure={(failure, taskName) => {
          setFailureError('')
          setSelectedFailure({ failure, taskName })
        }}
      />

      <WeeklyFailureAnalysisModal
        show={Boolean(selectedFailure)}
        failure={selectedFailure?.failure || null}
        taskName={selectedFailure?.taskName || ''}
        catalog={cncCatalog}
        members={programmation.users || []}
        saving={failureSaving}
        error={failureError}
        onHide={() => setSelectedFailure(null)}
        onAnalyze={analyzeSelectedFailure}
        onCreateAction={createSelectedFailureAction}
      />

      <TasksImporterUnified
        show={showTasksImporter}
        projectId={project._id}
        onHide={() => setShowTasksImporter(false)}
        onImported={handleTaskListImported}
      />

      <PdfExportModal
        show={showPdfExportModal}
        plan="weekly"
        navigator={navigator}
        project_id={showPdfExportModal ? project_id : undefined}
        items={weekly.ganttItems}
        nonWorkingDays={nonWorkingDays}
        users={programmation.users}
        filters={filters}
        columns={columns.map((column: any) => {

          let key = column.field

          if (key === 'position') {
            key = 'id'
          } else if (key === 'name') {
            key = 'title'
          }

          return {
            key,
            label: column.label,
            width: column.width
          }
        })}
        collapsedGroups={weekly.collapsedGroups}
        collapsedTasks={weekly.collapsedTasks}
        onHide={() => setShowPdfExportModal(false)}
      />
    </ProjectLayout>
  )
}

export default WeeklyPage;
